/**
 * FR-120…126 · Hồ sơ & bảo mật.
 *
 * Bám đúng bố cục `profile.html`:
 *   page-head (chip trạng thái 2FA + nút Lưu thay đổi)
 *   card[tabs]
 *     panel info     → .grid.g-side  : card "Hồ sơ" | stack[ card "Tài khoản", card "Điểm bảo mật" ]
 *     panel security → .grid.g-2     : card "Đổi mật khẩu" | stack[ card "Xác minh hai bước", card "Mã khôi phục" ]
 *     panel sessions → .table-wrap + .card-foot
 *     panel prefs    → card "Nhận thông báo qua email" với .setting-row
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError, thanhApiError, useLoiApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n, useT } from '../lib/i18n';
import { avatarTone, hienIp, hienViTri, initialsOf, useFormat } from '../lib/format';
import type {
  AccountSummary,
  CurrentUser,
  SecurityLogEntry,
  SessionInfo,
  TwoFactorSetup,
} from '../lib/types';
import { Icon } from '../components/Icon';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Modal } from '../components/Modal';
import { ApiErrorAlert } from '../components/ApiErrorAlert';
import { Field, SelectField } from '../components/Field';
import { PasswordStrength } from '../components/PasswordStrength';
import { AsyncSection, EmptyState, SkeletonCard } from '../components/States';
import { useToast } from '../components/Toast';
import { PasskeyPanel } from './PasskeyPanel';

const TABS = ['info', 'security', 'sessions', 'prefs'] as const;
type Tab = (typeof TABS)[number];

/**
 * OQ-258 — hai khoá không có thư tương ứng đã được gỡ khỏi màn hình.
 *
 * `docs/07 §7.9` liệt kê chín loại thư hệ thống, và không có "bản ghi DNS bị
 * đổi" lẫn "tóm tắt hằng tuần" — không chỗ nào trong máy chủ đọc hai khoá ấy.
 *
 * `OQ-086` từng chọn cách hiện chúng ở trạng thái **vô hiệu hoá kèm giải thích**,
 * với lý do: công tắc bật được rồi vĩnh viễn không có tác dụng thì tệ hơn là
 * không hiện. Đúng, nhưng chủ dự án chỉ ra vế còn lại — một dòng "Chưa khả dụng"
 * nằm mãi trên màn hình cũng là rác, chỉ là loại rác trung thực hơn.
 *
 * Nay bỏ hẳn. Ngày `docs/07` định nghĩa hai loại thư đó thì thêm lại khoá vào
 * danh sách này là xong.
 */
const NOTIFY_KEYS = ['domain_shared', 'security_alerts'] as const;

const TIMEZONES = [
  ['Asia/Ho_Chi_Minh', '(GMT+7) Hồ Chí Minh'],
  ['Asia/Bangkok', '(GMT+7) Bangkok'],
  ['Asia/Singapore', '(GMT+8) Singapore'],
  ['Asia/Tokyo', '(GMT+9) Tokyo'],
  ['Europe/London', '(GMT+0) London'],
  ['Europe/Paris', '(GMT+1) Paris'],
  ['America/New_York', '(GMT-5) New York'],
  ['UTC', '(GMT+0) UTC'],
] as const;

export function ProfilePage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = TABS.includes(raw as Tab) ? (raw as Tab) : 'info';
  const { user, mustSetupTwoFactor, mustChangePassword } = useAuth();

  const summaryQuery = useQuery({
    queryKey: ['me', 'summary'],
    queryFn: () => api.get<AccountSummary>('/me/summary'),
  });

  const infoFormRef = useRef<HTMLFormElement>(null);

  if (!user) return <SkeletonCard rows={6} />;

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 data-od-id="profile-title">{t('profile.title')}</h1>
          <p className="lede">{t('profile.lede')}</p>
        </div>
        <div className="row">
          <span className={user.twofaEnabled ? 'chip ok' : 'chip warn'}>
            <span className="dot" />
            {user.twofaEnabled ? t('profile.2fa.chipOn') : t('profile.2fa.chipOff')}
          </span>
          {tab === 'info' ? (
            <button
              className="btn btn-primary"
              type="button"
              data-od-id="profile-save"
              onClick={() => infoFormRef.current?.requestSubmit()}
            >
              {t('common.saveChanges')}
            </button>
          ) : null}
        </div>
      </div>

      {mustChangePassword ? (
        <div
          className="scope-note warn"
          style={{ marginBottom: 14 }}
          data-od-id="buoc-doi-mat-khau"
        >
          <Icon name="key" size={17} />
          <span>{t('auth.mustChangePassword')}</span>
        </div>
      ) : null}

      {mustSetupTwoFactor ? (
        <div className="scope-note warn" style={{ marginBottom: 14 }}>
          <Icon name="warn" size={17} />
          <span>{t('auth.mustSetup2fa')}</span>
        </div>
      ) : null}

      <div className="card" data-od-id="profile-tabs">
        <div className="tabs" role="tablist">
          {TABS.map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={tab === name}
              onClick={() => setParams({ tab: name }, { replace: true })}
            >
              {t(`profile.tab.${name}`)}
            </button>
          ))}
        </div>

        {tab === 'info' ? (
          <div className="card-body" role="tabpanel" data-od-id="panel-info">
            <div className="grid g-side">
              <InfoCard user={user} formRef={infoFormRef} />
              <div className="stack">
                <AccountCard user={user} summary={summaryQuery.data} />
                <SecurityScoreCard summary={summaryQuery.data} />
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'security' ? (
          <div className="card-body" role="tabpanel" data-od-id="panel-security">
            <SecurityTab
              user={user}
              summary={summaryQuery.data}
              onChanged={() => void summaryQuery.refetch()}
            />
          </div>
        ) : null}

        {tab === 'sessions' ? <SessionsPanel /> : null}

        {tab === 'prefs' ? (
          <div className="card-body" role="tabpanel" data-od-id="panel-prefs">
            <PrefsCard user={user} />
          </div>
        ) : null}
      </div>
    </>
  );
}

/* ═══════════ Tab 1 · Thông tin cá nhân ═══════════ */

function InfoCard({
  user,
  formRef,
}: {
  user: CurrentUser;
  formRef: React.RefObject<HTMLFormElement>;
}) {
  const t = useT();
  const { setLocale } = useI18n();
  const { refresh } = useAuth();
  const { toast, toastError } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [fullName, setFullName] = useState(user.fullName);
  const [phone, setPhone] = useState(user.phone ?? '');
  const [timezone, setTimezone] = useState(user.timezone);
  const [locale, setLocaleValue] = useState(user.locale);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();
  const [showEmail, setShowEmail] = useState(false);

  const save = useMutation({
    mutationFn: () => api.patch('/me', { fullName, phone: phone || null, timezone, locale }),
    onSuccess: async () => {
      await refresh();
      setError(null);
      setLocale(locale === 'en' ? 'en' : 'vi');
      toast(t('profile.info.saved'));
    },
    onError: (err) => {
      setError(thanhApiError(err));
      toastError(err instanceof ApiError ? err.message : t('state.error.title'));
    },
  });

  const uploadAvatar = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('avatar', file);
      return api.upload<{ avatarUrl: string }>('/me/avatar', form);
    },
    onSuccess: async () => {
      await refresh();
      toast(t('profile.info.saved'));
    },
    onError: (err) => toastError(err instanceof ApiError ? err.message : t('state.error.title')),
  });

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t('profile.info.heading')}</h2>
      </div>
      <div className="card-body stack">
        <form
          className="stack"
          ref={formRef}
          onChange={xoaLoiKhiGo.onChange}
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            save.mutate();
          }}
          data-od-id="form-profile"
        >
          <div className="row" style={{ gap: 14 }}>
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt=""
                width={48}
                height={48}
                style={{
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '1px solid var(--border)',
                }}
              />
            ) : (
              <span className={`avatar lg ${avatarTone(user.id)}`}>
                {initialsOf(user.fullName, user.email)}
              </span>
            )}
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) uploadAvatar.mutate(file);
                  event.target.value = '';
                }}
              />
              <button
                className="btn btn-sm"
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploadAvatar.isPending}
              >
                {uploadAvatar.isPending ? t('common.processing') : t('profile.info.avatarUpload')}
              </button>
              <div className="small muted" style={{ marginTop: 6 }}>
                {t('profile.info.avatarHint')}
              </div>
            </div>
          </div>

          <hr className="divider" style={{ margin: '6px 0' }} />

          <div className="grid g-2">
            <Field
              label={t('profile.info.fullName')}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              minLength={2}
              error={error?.fieldMessage('fullName')}
            />
            <Field
              label={t('profile.info.phone')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              error={error?.fieldMessage('phone')}
            />
          </div>

          <div className="field">
            <label htmlFor="pf-email">{t('profile.info.emailLogin')}</label>
            <div className="row" style={{ gap: 8 }}>
              <input id="pf-email" type="email" value={user.email} readOnly className="grow" />
              <button className="btn btn-sm" type="button" onClick={() => setShowEmail(true)}>
                {t('profile.info.changeEmail')}
              </button>
            </div>
            <span className="hint">{t('profile.info.emailHint')}</span>
          </div>

          <div className="grid g-2">
            <SelectField
              label={t('profile.info.timezone')}
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {TIMEZONES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectField>
            <SelectField
              label={t('profile.info.locale')}
              value={locale}
              onChange={(e) => setLocaleValue(e.target.value)}
            >
              <option value="vi">Tiếng Việt</option>
              <option value="en">English</option>
            </SelectField>
          </div>

          {/*
            OQ-288 — công tắc "Giao diện tối" ĐÃ BỎ khỏi đây.
            Nó trùng với nút chọn chế độ trên thanh header, mà nút ấy nay có ba
            lựa chọn (sáng · tối · tự động). Giữ cả hai thì công tắc hai trạng
            thái này không thể diễn tả "tự động" — và người dùng thấy hai chỗ
            nói về cùng một thứ theo hai cách khác nhau.
          */}
        </form>{' '}
      </div>

      {showEmail ? <ChangeEmailModal onClose={() => setShowEmail(false)} /> : null}
    </div>
  );
}

function AccountCard({ user, summary }: { user: CurrentUser; summary?: AccountSummary }) {
  const { formatDateTime } = useFormat();
  const t = useT();
  return (
    <div className="card">
      <div className="card-head">
        <h2>{t('profile.account.heading')}</h2>
      </div>
      <div className="card-body">
        <dl className="kv">
          <dt>{t('profile.account.role')}</dt>
          <dd>
            <span className="chip accent tag-role">{user.role.name}</span>
          </dd>
          <dt>{t('profile.account.email')}</dt>
          <dd className="mono">{user.email}</dd>
          <dt>{t('profile.account.joined')}</dt>
          <dd className="num">{summary ? formatDateTime(summary.joinedAt, user.timezone) : '—'}</dd>
          <dt>{t('profile.account.domains')}</dt>
          <dd className="num">{summary ? summary.domainCount : '—'}</dd>
          <dt>{t('profile.account.timezone')}</dt>
          <dd>{user.timezone}</dd>
        </dl>
      </div>
    </div>
  );
}

function SecurityScoreCard({ summary }: { summary?: AccountSummary }) {
  const t = useT();
  if (!summary) {
    return (
      <div className="card">
        <div className="card-head">
          <h2>{t('profile.score.heading')}</h2>
        </div>
        <div className="card-body">
          <SkeletonCard rows={2} />
        </div>
      </div>
    );
  }

  const met = summary.security.filter((s) => s.met).length;
  const total = summary.security.length;
  const percent = Math.round((met / total) * 100);
  const tone = percent >= 75 ? 'ok' : percent >= 50 ? 'warn' : 'bad';

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t('profile.score.heading')}</h2>
      </div>
      <div className="card-body">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="small muted">{t('profile.score.met', { met, total })}</span>
          <span className={`chip ${tone}`}>{t(`profile.score.level.${tone}`)}</span>
        </div>
        <div className={`bar ${tone === 'bad' ? '' : tone} mt-14`}>
          <span style={{ width: `${percent}%` }} />
        </div>
        <ul
          style={{
            margin: '14px 0 0',
            paddingLeft: 18,
            fontSize: 12.5,
            color: 'var(--muted)',
            lineHeight: 1.9,
          }}
        >
          {summary.security.map((item) => (
            <li key={item.id} style={item.met ? undefined : { color: 'var(--fg)' }}>
              {t(`profile.score.${item.id}`)}
              {item.met ? '' : ` — ${t('profile.score.todo')}`}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ChangeEmailModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const { toast } = useToast();
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const mutation = useMutation({
    mutationFn: () => api.post('/me/email/change', { newEmail, password }),
    onSuccess: () => {
      toast(t('profile.info.emailSent'));
      onClose();
    },
    onError: (err) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('profile.info.changeEmail')}
      subtitle={t('profile.info.emailHint')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.processing') : t('common.confirm')}
          </button>
        </>
      }
    >
      <ApiErrorAlert error={error} />
      <Field
        label={t('profile.info.newEmail')}
        type="email"
        value={newEmail}
        onChange={(e) => setNewEmail(e.target.value)}
        required
        autoFocus
        error={error?.fieldMessage('newEmail')}
      />
      <Field
        label={t('profile.info.currentPasswordToConfirm')}
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        required
        error={error?.fieldMessage('currentPassword') ?? error?.fieldMessage('password')}
      />
    </Modal>
  );
}

/* ═══════════ Tab 2 · Mật khẩu & 2FA ═══════════ */

function SecurityTab({
  user,
  summary,
  onChanged,
}: {
  user: CurrentUser;
  summary?: AccountSummary;
  onChanged: () => void;
}) {
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [showDisable, setShowDisable] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const { refresh } = useAuth();
  const { toast } = useToast();
  const t = useT();

  return (
    <>
      {/*
        OQ-331 — bố cục hai cột theo yêu cầu của chủ dự án:
        *"đưa block xác minh 2 bước, mã khôi phục và Passkey xuống dưới Đổi Mật
        khẩu, đưa nhật ký bảo mật sang cột thứ 2"*.

        Cột trái gom mọi thứ NGƯỜI DÙNG THAY ĐỔI — mật khẩu, 2FA, mã khôi phục,
        passkey. Cột phải là thứ chỉ để ĐỌC. Chia theo việc làm, không theo kích
        thước, nên thêm khối mới sau này cũng biết ngay nó thuộc cột nào.

        `alignItems: 'start'` để hai cột không kéo nhau cao bằng nhau — nhật ký
        dài hơn nhiều, và nếu kéo theo thì cột trái sẽ có một khoảng trống lớn
        dưới ô cuối.
      */}
      <div className="grid g-2" style={{ alignItems: 'start' }}>
        <div className="stack">
          <ChangePasswordCard onChanged={onChanged} />
          <TwoFactorCard
            user={user}
            summary={summary}
            onSetup={setSetup}
            onDisable={() => setShowDisable(true)}
          />
          <RecoveryCodesCard
            summary={summary}
            twofaOn={user.twofaEnabled}
            onGenerated={setRecoveryCodes}
          />
          <PasskeyPanel />
        </div>

        <SecurityLogCard />
      </div>

      {setup ? (
        <TwoFactorSetupModal
          setup={setup}
          onClose={() => setSetup(null)}
          onDone={async (codes) => {
            setSetup(null);
            setRecoveryCodes(codes);
            await refresh();
            onChanged();
            toast(t('profile.2fa.enabled'));
          }}
        />
      ) : null}

      {showDisable ? (
        <DisableTwoFactorModal
          onClose={() => setShowDisable(false)}
          onDone={async () => {
            setShowDisable(false);
            await refresh();
            onChanged();
            toast(t('profile.2fa.disabled'));
          }}
        />
      ) : null}

      {recoveryCodes ? (
        <RecoveryCodesModal
          codes={recoveryCodes}
          onClose={() => {
            setRecoveryCodes(null);
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

function ChangePasswordCard({ onChanged }: { onChanged: () => void }) {
  const t = useT();
  const { logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();
  const [daDoi, setDaDoi] = useState(false);

  /*
   * So khớp NGAY khi gõ, không đợi bấm nút.
   *
   * Bản trước chỉ so lúc submit và giữ kết quả trong state `mismatch` — nên
   * người dùng sửa xong ô thứ hai mà dòng "Hai mật khẩu không khớp" vẫn còn,
   * cho tới lần bấm nút kế tiếp. Tính thẳng từ giá trị hiện tại thì nó tự đúng
   * ở mọi lần gõ và không có state nào để lỡ quên dọn.
   *
   * Chỉ báo khi ô thứ hai đã có chữ: gõ ô thứ nhất xong mà đã đỏ ngay thì
   * thông báo ấy nói về một việc người dùng chưa kịp làm.
   */
  const lechNhau = confirm.length > 0 && next !== confirm;

  const mutation = useMutation({
    mutationFn: () => api.post('/me/password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
      setError(null);
      onChanged();
      /*
       * KHÔNG đăng xuất ngay: hiện hộp thoại để người dùng đọc xong rồi tự bấm.
       *
       * Đá họ ra màn đăng nhập giữa chừng thì họ không kịp biết vì sao — và
       * "bị đăng xuất đột ngột" là thứ trông giống hệt một lỗi.
       */
      setDaDoi(true);
    },
    onError: (err) => setError(thanhApiError(err)),
  });

  /* Bấm Đồng ý: thu hồi MỌI phiên (kể cả phiên này) rồi về màn đăng nhập. */
  const ketThuc = useMutation({
    mutationFn: () => api.del('/me/sessions?all=true'),
    onSettled: async () => {
      /*
       * `onSettled` chứ không `onSuccess`: thu hồi hỏng thì vẫn phải đăng xuất
       * phía client. Kẹt lại trong app với một phiên dựa trên mật khẩu đã bỏ là
       * kết cục tệ hơn hẳn việc một lời gọi dọn dẹp thất bại.
       */
      await logout();
    },
  });

  return (
    <div className="card">
      <div className="card-head">
        <div className="grow">
          <h2>{t('profile.password.heading')}</h2>
          <div className="sub">{t('profile.password.sub')}</div>
        </div>
      </div>
      <div className="card-body">
        <form
          className="stack"
          data-od-id="form-password"
          onChange={xoaLoiKhiGo.onChange}
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (lechNhau || next !== confirm) return;
            mutation.mutate();
          }}
        >
          <ApiErrorAlert error={error} />

          <Field
            label={t('profile.password.current')}
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
            error={error?.fieldMessage('currentPassword')}
          />
          <Field
            label={t('profile.password.new')}
            type="password"
            value={next}
            onChange={(e) => {
              setNext(e.target.value);
              /*
               * Xoá lỗi của MÁY CHỦ ngay khi người dùng gõ tiếp.
               *
               * "Mật khẩu này đã xuất hiện trong các vụ lộ dữ liệu" là một phán
               * quyết về CHUỖI CŨ. Giữ nó lại khi chuỗi đã đổi là nói một điều
               * chưa ai kiểm — người dùng đọc thành "mật khẩu mới cũng lộ", và
               * họ không có cách nào biết là không phải.
               */
              if (error) setError(null);
            }}
            autoComplete="new-password"
            required
            error={error?.fieldMessage('newPassword')}
            trailing={<PasswordStrength value={next} />}
          />
          <Field
            label={t('profile.password.confirm')}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            error={lechNhau ? t('auth.mismatch') : undefined}
          />

          <div className="setting-row" style={{ paddingTop: 0 }}>
            <div className="grow">
              <b>{t('profile.password.signOutOthers')}</b>
              <div className="sub">{t('profile.password.signOutOthersSub')}</div>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked
                disabled
                aria-label={t('profile.password.signOutOthers')}
              />
              <span className="track" />
            </label>
          </div>

          <div>
            <button className="btn btn-primary" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t('common.processing') : t('profile.password.submit')}
            </button>
          </div>
        </form>
      </div>

      {daDoi ? (
        <Modal
          open
          title={t('profile.password.doneTitle')}
          onClose={() => {
            /*
             * Đóng bằng Esc hay bấm ra ngoài cũng phải kết thúc phiên — không
             * để một lối thoát nào giữ người dùng lại trong app với phiên cũ.
             */
            if (!ketThuc.isPending) ketThuc.mutate();
          }}
          footer={
            <button
              className="btn btn-primary"
              type="button"
              disabled={ketThuc.isPending}
              onClick={() => ketThuc.mutate()}
              data-od-id="btn-dong-y-dang-nhap-lai"
            >
              {ketThuc.isPending ? t('common.processing') : t('common.agree')}
            </button>
          }
        >
          <div data-od-id="thong-bao-doi-mat-khau">
            <div className="form-alert ok">
              <Icon name="check" size={17} />
              <span>{t('profile.password.doneMessage')}</span>
            </div>
            <p className="hint">{t('profile.password.doneNote')}</p>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function TwoFactorCard({
  user,
  summary,
  onSetup,
  onDisable,
}: {
  user: CurrentUser;
  summary?: AccountSummary;
  onSetup: (setup: TwoFactorSetup) => void;
  onDisable: () => void;
}) {
  const { formatDateTime } = useFormat();
  const t = useT();
  const { toastError } = useToast();

  const start = useMutation({
    mutationFn: () => api.post<TwoFactorSetup>('/me/2fa/setup'),
    onSuccess: onSetup,
    onError: (err) => toastError(err instanceof ApiError ? err.message : t('state.error.title')),
  });

  return (
    <div className="card">
      <div className="card-head">
        <div className="grow">
          <h2>{t('profile.2fa.heading')}</h2>
          <div className="sub">{t('profile.2fa.sub')}</div>
        </div>
        <span className={user.twofaEnabled ? 'chip ok' : 'chip warn'}>
          <span className="dot" />
          {user.twofaEnabled ? t('common.enabled') : t('common.disabled')}
        </span>
      </div>
      <div className="card-body">
        <div className="setting-row">
          <div className="grow">
            <b>{t('profile.2fa.totp')}</b>
            <div className="sub">
              {user.twofaEnabled && summary?.twofaConfirmedAt
                ? t('profile.2fa.setupOn', {
                    date: formatDateTime(summary.twofaConfirmedAt, user.timezone),
                  })
                : t('profile.2fa.notSet')}
            </div>
          </div>
          <div className="row">
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => start.mutate()}
              disabled={start.isPending}
            >
              {user.twofaEnabled ? t('profile.2fa.reconfigure') : t('profile.2fa.enable')}
            </button>
            {user.twofaEnabled ? (
              <button className="btn btn-sm btn-danger" type="button" onClick={onDisable}>
                {t('profile.2fa.off')}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function RecoveryCodesCard({
  summary,
  twofaOn,
  onGenerated,
}: {
  summary?: AccountSummary;
  /** OQ-257 — mã khôi phục chỉ có nghĩa khi 2FA đang bật. */
  twofaOn: boolean;
  onGenerated: (codes: string[]) => void;
}) {
  const t = useT();
  const { toastError } = useToast();
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState('');

  const regenerate = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/me/2fa/recovery-codes', { password }),
    onSuccess: (data) => {
      setAskPassword(false);
      setPassword('');
      onGenerated(data.recoveryCodes);
    },
    onError: (err) => toastError(err instanceof ApiError ? err.message : t('state.error.title')),
  });

  const remaining = summary?.recoveryCodesRemaining ?? 0;
  const total = summary?.recoveryCodesTotal ?? 0;
  const tone = total === 0 ? 'warn' : remaining <= 3 ? 'bad' : 'ok';

  return (
    <div className="card">
      <div className="card-head">
        <div className="grow">
          <h2>{t('profile.recovery.heading')}</h2>
          <div className="sub">{t('profile.recovery.sub')}</div>
        </div>
        <span className={`chip ${tone}`}>
          <span className="dot" />
          {total === 0
            ? t('profile.recovery.none')
            : t('profile.recovery.remaining', { remaining, total })}
        </span>
      </div>
      <div className="card-body stack">
        {/*
          OQ-257 — chưa bật 2FA thì không có gì để khôi phục.
          Giữ thẻ lại thay vì ẩn cả: người dùng vẫn nên biết mã khôi phục là gì
          và vì sao mình chưa có. Ẩn sạch thì đến lúc bật 2FA họ mới thấy một
          thẻ lạ xuất hiện.
        */}
        <p className="small muted" style={{ margin: 0 }} data-od-id="recovery-note">
          {twofaOn ? t('profile.recovery.hidden') : t('profile.recovery.need2fa')}
        </p>
        {twofaOn ? (
          <div className="row">
            <button
              className="btn btn-sm"
              type="button"
              data-od-id="recovery-regenerate"
              onClick={() => setAskPassword(true)}
            >
              {t('profile.recovery.regenerate')}
            </button>
          </div>
        ) : null}
      </div>

      {askPassword ? (
        <Modal
          open
          title={t('profile.recovery.regenerate')}
          subtitle={t('profile.recovery.warn')}
          onClose={() => setAskPassword(false)}

          onSubmit={() => regenerate.mutate()}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setAskPassword(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" type="submit" disabled={regenerate.isPending}>
                {regenerate.isPending ? t('common.processing') : t('common.confirm')}
              </button>
            </>
          }
        >
          <Field
            label={t('profile.info.currentPasswordToConfirm')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            autoFocus
          />
        </Modal>
      ) : null}
    </div>
  );
}

function SecurityLogCard() {
  const { formatDateTime, formatRelative } = useFormat();
  const t = useT();
  const query = useQuery({
    queryKey: ['me', 'security-log'],
    queryFn: () => api.get<SecurityLogEntry[]>('/me/security-log'),
  });

  return (
    <div className="card">
      <div className="card-head">
        <div className="grow">
          <h2>{t('profile.securityLog.heading')}</h2>
          <div className="sub">{t('profile.securityLog.sub')}</div>
        </div>
        <button
          className="icon-btn"
          type="button"
          aria-label={t('common.retry')}
          onClick={() => void query.refetch()}
        >
          <Icon name="refresh" size={16} />
        </button>
      </div>

      <AsyncSection
        loading={query.isLoading}
        error={query.error}
        data={query.data}
        onRetry={() => void query.refetch()}
        empty={<EmptyState title={t('state.empty.title')} />}
      >
        {(entries) => (
          <div className="table-wrap">
            <table className="tbl" data-od-id="security-log">
              <thead>
                <tr>
                  <th scope="col">{t('profile.securityLog.action')}</th>
                  <th scope="col">{t('profile.sessions.ip')}</th>
                  <th scope="col">{t('profile.sessions.lastSeen')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      <span className={entry.result === 'failure' ? 'chip bad' : 'chip'}>
                        <span className="dot" />
                        {t(`action.${entry.action}`)}
                      </span>
                    </td>
                    <td className="mono">
                      {hienIp(entry.ip, {
                        'may-nay': t('profile.sessions.ipLoopback'),
                        'noi-bo': '',
                        'cong-cong': '',
                      })}
                    </td>
                    <td className="small num" title={formatDateTime(entry.createdAt)}>
                      {formatRelative(entry.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>
    </div>
  );
}

function TwoFactorSetupModal({
  setup,
  onClose,
  onDone,
}: {
  setup: TwoFactorSetup;
  onClose: () => void;
  onDone: (codes: string[]) => void | Promise<void>;
}) {
  const t = useT();
  const [code, setCode] = useState('');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ recoveryCodes: string[] }>('/me/2fa/confirm', { code: code.trim() }),
    onSuccess: (data) => void onDone(data.recoveryCodes),
    onError: (err) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      wide
      title={t('profile.2fa.setupTitle')}
      subtitle={t('profile.2fa.setupStep1')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <span className="grow" />
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.processing') : t('profile.2fa.confirm')}
          </button>
        </>
      }
    >
      <div className="row" style={{ gap: 18, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
        <div className="qr-box" dangerouslySetInnerHTML={{ __html: setup.qrSvg }} />

        <div className="stack" style={{ minWidth: 0 }}>
          <div className="field">
            <label>{t('profile.2fa.setupStep2')}</label>
            <div className="copy-line">
              <span className="grow mono">{setup.secret}</span>
              <button
                className="btn btn-sm btn-ghost"
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(setup.secret);
                  setCopied(true);
                }}
              >
                {copied ? t('common.copied') : t('common.copy')}
              </button>
            </div>
          </div>

          <Field
            label={t('profile.2fa.setupStep3')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            maxLength={6}
            required
            autoFocus
            placeholder="000000"
            error={
              error?.fieldMessage('code') ??
              (error?.fields.length === 0 ? error.message : undefined)
            }
            style={{
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.3em',
              fontSize: 16,
              height: 42,
            }}
          />

          <div className="scope-note accent">
            <Icon name="info" size={17} />
            <span>{t('profile.2fa.recoveryNote')}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function DisableTwoFactorModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const t = useT();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const mutation = useMutation({
    mutationFn: () => api.post('/me/2fa/disable', { password, code: code.trim() }),
    onSuccess: () => void onDone(),
    onError: (err) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('profile.2fa.disableTitle')}
      subtitle={t('profile.2fa.disableWarn')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('profile.2fa.keep')}
          </button>
          <button className="btn btn-danger" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.processing') : t('profile.2fa.off')}
          </button>
        </>
      }
    >
      <ApiErrorAlert error={error} />
      <Field
        label={t('profile.info.currentPasswordToConfirm')}
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        required
        autoFocus
        error={error?.fieldMessage('currentPassword')}
      />
      <Field
        label={t('profile.2fa.currentCode')}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        inputMode="numeric"
        maxLength={6}
        required
        placeholder="000000"
        error={error?.fieldMessage('code')}
        style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.3em' }}
      />
    </Modal>
  );
}

function RecoveryCodesModal({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const t = useT();
  const [saved, setSaved] = useState(false);

  return (
    <Modal
      open
      title={t('profile.recovery.heading')}
      subtitle={t('profile.recovery.warn')}
      onClose={() => saved && onClose()}
      footer={
        <>
          <button
            className="btn"
            type="button"
            onClick={() => {
              const blob = new Blob([codes.join('\n')], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'dnshub-recovery-codes.txt';
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            {t('common.download')}
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => void navigator.clipboard?.writeText(codes.join('\n'))}
          >
            {t('common.copy')}
          </button>
          <span className="grow" />
          <button className="btn btn-primary" type="button" disabled={!saved} onClick={onClose}>
            {t('common.close')}
          </button>
        </>
      }
    >
      <div className="codes" aria-label={t('profile.recovery.heading')}>
        {codes.map((code) => (
          <code key={code}>{code}</code>
        ))}
      </div>
      <label className="row" style={{ gap: 8, cursor: 'pointer', marginTop: 6 }}>
        <input
          className="check"
          type="checkbox"
          checked={saved}
          onChange={(e) => setSaved(e.target.checked)}
        />
        <span className="small">{t('profile.recovery.saved')}</span>
      </label>
    </Modal>
  );
}

/* ═══════════ Tab 3 · Phiên & thiết bị ═══════════ */

function SessionsPanel() {
  const { formatDateTime, formatRelative } = useFormat();
  const t = useT();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const query = useQuery({
    queryKey: ['me', 'sessions'],
    queryFn: () => api.get<SessionInfo[]>('/me/sessions'),
  });

  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['me', 'sessions'] });

  const endOne = useMutation({
    mutationFn: (id: string) => api.del(`/me/sessions/${id}`),
    onSuccess: async () => {
      await invalidate();
      toast(t('profile.sessions.ended'));
    },
    onError,
  });

  const [hoiDangXuat, setHoiDangXuat] = useState(false);

  const endOthers = useMutation({
    mutationFn: () => api.del<{ revoked: number }>('/me/sessions'),
    onSuccess: async (data) => {
      await invalidate();
      toast(t('profile.sessions.endedOthers', { count: data.revoked }));
    },
    onError,
  });

  return (
    <div role="tabpanel" data-od-id="panel-sessions">
      <AsyncSection
        loading={query.isLoading}
        error={query.error}
        data={query.data}
        onRetry={() => void query.refetch()}
        empty={<EmptyState title={t('state.empty.title')} />}
      >
        {(sessions) => (
          <div className="table-wrap">
            <table className="tbl" data-od-id="sessions-table">
              <thead>
                <tr>
                  <th scope="col">{t('profile.sessions.device')}</th>
                  <th scope="col">{t('profile.sessions.location')}</th>
                  <th scope="col">{t('profile.sessions.ip')}</th>
                  <th scope="col">{t('profile.sessions.lastSeen')}</th>
                  <th scope="col" className="right">
                    {t('profile.sessions.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.id}>
                    <td>
                      <b>{session.deviceLabel ?? t('common.unknown')}</b>
                      <div className="sub">
                        {session.current
                          ? t('profile.sessions.current')
                          : /*
                             * KHÔNG hiện User-Agent thô. Chuỗi ấy dài 120+ ký tự,
                             * bị cắt giữa chừng thành `… Win64; x64) AppleW` —
                             * một mẩu không nói gì mà chiếm chỗ như thể có nói.
                             *
                             * `deviceLabel` đã là bản rút gọn của chính nó
                             * ("Chrome · Windows"), nên dòng phụ chỉ cần nói phiên
                             * này không phải phiên đang dùng.
                             */
                            t('profile.sessions.other')}
                      </div>
                    </td>
                    <td>
                      {hienViTri(session.location, session.ip, {
                        'may-nay': t('profile.sessions.locThisMachine'),
                        'noi-bo': t('profile.sessions.locLan'),
                        'cong-cong': t('profile.sessions.locUnknown'),
                        khongRo: t('profile.sessions.locUnknown'),
                      })}
                    </td>
                    <td className="mono">
                      {hienIp(session.ip, {
                        'may-nay': t('profile.sessions.ipLoopback'),
                        'noi-bo': t('profile.sessions.locLan'),
                        'cong-cong': '',
                      })}
                    </td>
                    <td className="small num" title={formatDateTime(session.lastSeenAt)}>
                      {session.current
                        ? t('profile.sessions.active')
                        : formatRelative(session.lastSeenAt)}
                    </td>
                    <td className="right">
                      {session.current ? (
                        <span className="chip ok">
                          <span className="dot" />
                          {t('profile.sessions.thisDevice')}
                        </span>
                      ) : (
                        <button
                          className="btn btn-sm btn-danger"
                          type="button"
                          onClick={() => endOne.mutate(session.id)}
                          disabled={endOne.isPending}
                        >
                          {t('profile.sessions.end')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      <div className="card-foot">
        <span className="small muted">{t('profile.sessions.expiryNote')}</span>
        <span style={{ flex: 1 }} />
        <button
          className="btn btn-sm btn-danger"
          type="button"
          onClick={() => setHoiDangXuat(true)}
          disabled={endOthers.isPending || (query.data?.length ?? 0) < 2}
        >
          {t('profile.sessions.endOthers')}
        </button>

        <ConfirmDialog
          open={hoiDangXuat}
          danger
          busy={endOthers.isPending}
          odId="xac-nhan-dang-xuat-khac"
          title={t('profile.sessions.endOthers')}
          message={t('profile.sessions.endOthersConfirm')}
          undoNote={t('profile.sessions.endOthersUndo')}
          confirmLabel={t('profile.sessions.endOthers')}
          onCancel={() => setHoiDangXuat(false)}
          onConfirm={() => {
            setHoiDangXuat(false);
            endOthers.mutate();
          }}
        />
      </div>
    </div>
  );
}

/* ═══════════ Tab 4 · Thông báo ═══════════ */

function PrefsCard({ user }: { user: CurrentUser }) {
  const t = useT();
  const { refresh } = useAuth();
  const { toast, toastError } = useToast();

  /**
   * `users.notify_prefs` mặc định là `{}` (docs/03 §3.2), nên khoá vắng mặt là
   * trạng thái bình thường của mọi tài khoản mới — và theo quy tắc đã chốt cho
   * toàn dự án, vắng mặt nghĩa là **có gửi**. Chỉ `false` tường minh mới tắt.
   *
   * Đọc bằng `Boolean(...)` sẽ khiến tài khoản mới thấy cả bốn công tắc tắt
   * trong khi máy chủ vẫn gửi thư — lệch giữa cái người dùng thấy và cái hệ
   * thống làm.
   */
  const readPrefs = () => {
    const base: Record<string, boolean> = {};
    for (const key of NOTIFY_KEYS) base[key] = user.notifyPrefs?.[key] !== false;
    return base;
  };

  const [prefs, setPrefs] = useState<Record<string, boolean>>(readPrefs);

  useEffect(() => {
    setPrefs(readPrefs());
  }, [user.notifyPrefs]);

  const save = useMutation({
    mutationFn: (next: Record<string, boolean>) => api.patch('/me', { notifyPrefs: next }),
    onSuccess: async () => {
      await refresh();
      toast(t('profile.info.saved'));
    },
    onError: (err) => toastError(err instanceof ApiError ? err.message : t('state.error.title')),
  });

  return (
    <div className="card">
      <div className="card-head">
        <div className="grow">
          <h2>{t('profile.prefs.heading')}</h2>
          <div className="sub">{t('profile.prefs.sub')}</div>
        </div>
      </div>
      <div className="card-body">
        {NOTIFY_KEYS.map((key) => (
          <div className="setting-row" key={key}>
            <div className="grow">
              <b>{t(`profile.prefs.${key}`)}</b>
              <div className="sub">{t(`profile.prefs.${key}.sub`)}</div>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={prefs[key] ?? false}
                aria-label={t(`profile.prefs.${key}`)}
                onChange={(event) => setPrefs({ ...prefs, [key]: event.target.checked })}
              />
              <span className="track" />
            </label>
          </div>
        ))}

        <div className="setting-row">
          <div className="grow">
            <b>{t('profile.prefs.new_device')}</b>
            <div className="sub">{t('profile.prefs.new_device.sub')}</div>
          </div>
          <label className="switch">
            <input type="checkbox" checked disabled aria-label={t('profile.prefs.new_device')} />
            <span className="track" />
          </label>
        </div>
      </div>
      <div className="card-foot">
        <span style={{ flex: 1 }} />
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => save.mutate(prefs)}
          disabled={save.isPending}
        >
          {save.isPending ? t('common.processing') : t('common.saveChanges')}
        </button>
      </div>
    </div>
  );
}
